import React, { useState } from "react";
import { Box, Collapse, Divider } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { StriimTypography } from "@striim/striim-ui";

export interface ConnectionValidationResult {
    kind: "SOURCE" | "TARGET";
    componentName: string;
    adapter: string;
    type: string;
    success: boolean;
    message: string;
}

interface Props {
    results: ConnectionValidationResult[];
}

const styles = {
    root: (allSuccess: boolean, allFailed: boolean) => ({
        border: "1px solid",
        borderColor: allFailed ? "critical.500" : allSuccess ? "success.500" : "warning.500",
        borderRadius: 1,
        backgroundColor: allFailed ? "critical.50" : allSuccess ? "success.50" : "warning.50"
    }),
    summaryRow: {
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        p: 1.5,
        cursor: "pointer",
        userSelect: "none" as const
    },
    summaryText: {
        flex: 1,
        fontWeight: 600,
        color: "greyscale.900"
    },
    chevron: {
        fontSize: 18,
        color: "greyscale.600",
        flexShrink: 0
    },
    details: {
        px: 1.5,
        pb: 1.5
    },
    sectionLabel: {
        display: "block",
        fontWeight: 600,
        color: "greyscale.700",
        textTransform: "uppercase" as const,
        letterSpacing: "0.5px",
        mb: 0.5
    },
    itemRow: {
        display: "flex",
        alignItems: "flex-start",
        gap: 0.75,
        py: 0.4
    },
    itemIndex: {
        color: "greyscale.500",
        minWidth: 16,
        textAlign: "right" as const,
        flexShrink: 0,
        mt: "1px"
    },
    itemBody: {
        flex: 1,
        display: "flex",
        flexDirection: "column" as const
    },
    itemName: {
        fontWeight: 500,
        color: "greyscale.900",
        lineHeight: 1.3
    },
    itemType: {
        color: "greyscale.600",
        lineHeight: 1.3
    },
    statusIcon: (success: boolean) => ({
        fontSize: 15,
        color: success ? "success.500" : "critical.500",
        flexShrink: 0,
        mt: "1px"
    }),
    summaryIcon: (allSuccess: boolean, allFailed: boolean) => ({
        fontSize: 16,
        color: allSuccess ? "success.500" : allFailed ? "critical.500" : "warning.500",
        flexShrink: 0
    }),
    errorSection: {
        mt: 1,
        pt: 1
    },
    errorDivider: {
        mb: 1,
        borderColor: "critical.200"
    },
    errorRow: {
        display: "flex",
        alignItems: "flex-start",
        gap: 0.75,
        py: 0.3
    },
    errorBullet: {
        color: "critical.500",
        flexShrink: 0,
        mt: "1px",
        fontSize: 14
    },
    errorText: {
        color: "critical.700",
        lineHeight: 1.4,
        flex: 1,
        wordBreak: "break-word"
    }
};

// Pretty-print the wizard type for display (e.g. "MYSQL" -> "MySQL").
const TYPE_LABELS: Record<string, string> = {
    MYSQL: "MySQL",
    MSSQL: "SQL Server",
    POSTGRES: "PostgreSQL",
    ORACLE: "Oracle",
    MARIADB: "MariaDB",
    MARIADBXPAND: "MariaDB Xpand",
    MONGODB: "MongoDB",
    YUGABYTE: "YugabyteDB",
    SNOWFLAKE: "Snowflake"
};

const formatType = (type: string): string => TYPE_LABELS[(type ?? "").toUpperCase()] ?? type;

interface GroupProps {
    label: string;
    items: ConnectionValidationResult[];
}

const Group: React.FC<GroupProps> = ({ label, items }) => (
    <Box>
        <StriimTypography variant="caption3" sx={styles.sectionLabel}>
            {label}
        </StriimTypography>
        {items.map((item, idx) => (
            <Box key={idx} sx={styles.itemRow}>
                <StriimTypography variant="body4" sx={styles.itemIndex}>
                    {idx + 1}.
                </StriimTypography>
                <Box sx={styles.itemBody}>
                    <StriimTypography variant="body4" sx={styles.itemName}>
                        {item.componentName}
                    </StriimTypography>
                    <StriimTypography variant="caption3" sx={styles.itemType}>
                        {formatType(item.type)}
                    </StriimTypography>
                </Box>
                {item.success ? (
                    <CheckCircleOutlineIcon sx={styles.statusIcon(true)} />
                ) : (
                    <ErrorOutlineIcon sx={styles.statusIcon(false)} />
                )}
            </Box>
        ))}
    </Box>
);

const ConnectionValidationResults: React.FC<Props> = ({ results }) => {
    const sources = results.filter(r => r.kind === "SOURCE");
    const targets = results.filter(r => r.kind === "TARGET");
    const failed = results.filter(r => !r.success && r.message);
    const passedCount = results.filter(r => r.success).length;
    const allSuccess = results.every(r => r.success);
    const allFailed = results.every(r => !r.success);

    // Expanded by default when something failed so the user sees the detail.
    const [open, setOpen] = useState(!allSuccess);

    const summaryText = allSuccess
        ? results.length === 1
            ? "Connection validated successfully"
            : `All ${results.length} connections validated successfully`
        : allFailed
        ? results.length === 1
            ? "Connection failed"
            : `All ${results.length} connections failed`
        : `${passedCount} of ${results.length} connections validated successfully`;

    return (
        <Box sx={styles.root(allSuccess, allFailed)}>
            <Box sx={styles.summaryRow} onClick={() => setOpen(prev => !prev)}>
                {allSuccess ? (
                    <CheckCircleOutlineIcon sx={styles.summaryIcon(allSuccess, allFailed)} />
                ) : (
                    <ErrorOutlineIcon sx={styles.summaryIcon(allSuccess, allFailed)} />
                )}
                <StriimTypography variant="body4" sx={styles.summaryText}>
                    {summaryText}
                </StriimTypography>
                {open ? <KeyboardArrowUpIcon sx={styles.chevron} /> : <KeyboardArrowDownIcon sx={styles.chevron} />}
            </Box>

            <Collapse in={open} timeout="auto" unmountOnExit>
                <Box sx={styles.details}>
                    {sources.length > 0 && (
                        <Box sx={{ mb: targets.length > 0 ? 1.25 : 0 }}>
                            <Group label="Sources" items={sources} />
                        </Box>
                    )}
                    {targets.length > 0 && <Group label="Targets" items={targets} />}
                    {failed.length > 0 && (
                        <Box sx={styles.errorSection}>
                            <Divider sx={styles.errorDivider} />
                            {failed.map((item, idx) => (
                                <Box key={idx} sx={styles.errorRow}>
                                    <ErrorOutlineIcon sx={styles.errorBullet} />
                                    <StriimTypography variant="body4" sx={styles.errorText}>
                                        <strong>{item.componentName}:</strong> {item.message}
                                    </StriimTypography>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>
            </Collapse>
        </Box>
    );
};

export default ConnectionValidationResults;
